import { z } from '@awpmarine/core/zod';

/** Schema for physical address values. */
export const PhysicalAddressSchema = z.object(
  {
    line1: z.string('Address line 1 is required').min(2),
    line2: z.string().nullable(),
    city: z.string('City is required'),
    region: z.string().nullable(),
    postcode: z.string().nullable(), // some countries don't have post codes
    country: z.string('Country is required').min(2),
    formatted: z.string('Formatted address is required').min(2),
  },
  'Invalid address',
);

/** Representation of physical address values. */
export type PhysicalAddress = z.infer<typeof PhysicalAddressSchema>;

type MakeFormattedPhysicalAddressOptions = Omit<PhysicalAddress, 'formatted'>;
/**
 * Create a `PhysicalAddress` object with a `formatted` string.
 * @param value - address fields
 * @param validate - whether to validate the combined object against the schema
 * @returns `PhysicalAddress` object with `formatted` field
 */
export function makeFormattedPhysicalAddress(
  value: MakeFormattedPhysicalAddressOptions,
  validate: true,
): PhysicalAddress | undefined;
export function makeFormattedPhysicalAddress(
  value: MakeFormattedPhysicalAddressOptions,
  validate?: false,
): PhysicalAddress;
export function makeFormattedPhysicalAddress(
  value: MakeFormattedPhysicalAddressOptions,
  validate: boolean = false,
): PhysicalAddress | undefined {
  const parts = [
    //
    value.line1,
    value.line2,
    value.city,
    value.region,
    value.postcode,
    value.country,
  ];
  const formatted = parts.filter(Boolean).join(', ');

  const combined = { ...value, formatted };
  if (validate) {
    const result = PhysicalAddressSchema.safeParse(combined);
    return result.success ? result.data : undefined;
  }

  return combined;
}

export type PlacePrediction = { id: string; address: PhysicalAddress };
